import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import CompanyDashboard from './pages/CompanyDashboard';
import CandidateDashboard from './pages/CandidateDashboard';
import AdminDashboard from './pages/AdminDashboard';
import JobPost from './pages/JobPost';
import JobDetails from './pages/JobDetails';
import RoleDetailPage from './pages/RoleDetailPage';
import CandidateProfile from './pages/CandidateProfile';
import Messages from './pages/Messages';
import TrialsPage from './pages/TrialsPage';
import AssessmentWizard from './pages/AssessmentWizard';
import TrialManagement from './pages/TrialManagement';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ForTalent from './pages/ForTalent';
import ForEmployers from './pages/ForEmployers';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CandidateTerms from './pages/CandidateTerms';
import CookiePolicy from './pages/CookiePolicy';
import ComplaintsDisputes from './pages/ComplaintsDisputes';

// ── Beta gate ────────────────────────────────────────────────────────────────
// Toggle off at full launch by setting REACT_APP_BETA_MODE=false — no code changes needed.
const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';

// Helper: returns true only for company accounts that explicitly bypass the beta gate.
const isDemoEmployer = (user) => user?.user_type === 'company' && user?.is_demo === true;

const LoadingScreen = () => (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
        <div className="text-slate-400 text-sm">Loading…</div>
    </div>
);

// Shown to authenticated company accounts that are NOT is_demo:true in beta mode.
const BetaRestricted = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => { await logout(); navigate('/login'); };
    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
            <div className="bg-white rounded-3xl shadow-sm p-10 border border-purple-200/50 max-w-md w-full text-center">
                <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto mx-auto mb-6" />
                <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Employer Access Coming Soon</h2>
                <p className="text-slate-500 mb-8 text-sm leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                    We're currently in our candidate-only beta. Employer accounts will be activated at full launch.
                </p>
                <button onClick={handleLogout} className="text-sm text-[#8B5CF6] hover:underline font-semibold" data-testid="beta-restricted-logout">
                    Sign out
                </button>
            </div>
        </div>
    );
};

// Shown for any URL that doesn't match a defined route.
const NotFound = () => {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }} data-testid="not-found-page">
            <div className="bg-white rounded-3xl shadow-sm p-10 border border-purple-200/50 max-w-md w-full text-center">
                <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto mx-auto mb-6" />
                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>404</span>
                <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Page not found</h2>
                <p className="text-slate-500 mb-8 text-sm leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                    The page you're looking for doesn't exist or may have moved.
                </p>
                <button onClick={() => navigate('/')} className="btn-primary rounded-xl px-6 py-3 text-sm font-semibold" data-testid="not-found-home-btn">
                    Back to Home
                </button>
            </div>
        </div>
    );
};

// Wraps public/marketing pages.
// Beta: unauthenticated → /login. Authenticated candidate → /candidate/dashboard.
// Demo employer and admin pass through normally.
const BetaGate = ({ children }) => {
    const { isAuthenticated, user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (BETA_MODE && !isAuthenticated) return <Navigate to="/login" replace />;
    if (BETA_MODE && user?.user_type === 'candidate') return <Navigate to="/candidate/dashboard" replace />;
    return children;
};

const PrivateRoute = ({ children, allowedTypes }) => {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) return <LoadingScreen />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;

    // Beta: non-demo company accounts cannot access any authenticated route.
    if (BETA_MODE && user?.user_type === 'company' && !isDemoEmployer(user)) {
        return <Navigate to="/beta-restricted" replace />;
    }

    if (allowedTypes && !allowedTypes.includes(user?.user_type)) {
        // In beta: candidates hitting company routes go to their own dashboard, not a 403.
        if (BETA_MODE && user?.user_type === 'candidate') return <Navigate to="/candidate/dashboard" replace />;
        return <Navigate to="/" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<BetaGate><LandingPage /></BetaGate>} />
            <Route path="/for-talent" element={<BetaGate><ForTalent /></BetaGate>} />
            <Route path="/for-employers" element={<BetaGate><ForEmployers /></BetaGate>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/beta-restricted" element={<BetaRestricted />} />

            {/* Legal pages — always public, no auth or beta gate required */}
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal/candidate-terms" element={<CandidateTerms />} />
            <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
            <Route path="/legal/complaints-disputes" element={<ComplaintsDisputes />} />
            
            <Route path="/company/dashboard" element={
                <PrivateRoute allowedTypes={['company']}>
                    <CompanyDashboard />
                </PrivateRoute>
            } />
            
            <Route path="/company/post-job" element={
                <PrivateRoute allowedTypes={['company']}>
                    <JobPost />
                </PrivateRoute>
            } />
            
            <Route path="/candidate/dashboard" element={
                <PrivateRoute allowedTypes={['candidate']}>
                    <CandidateDashboard />
                </PrivateRoute>
            } />
            
            <Route path="/candidate/profile" element={
                <PrivateRoute allowedTypes={['candidate']}>
                    <CandidateProfile />
                </PrivateRoute>
            } />
            
            <Route path="/candidate/assessment" element={
                <PrivateRoute allowedTypes={['candidate']}>
                    <AssessmentWizard />
                </PrivateRoute>
            } />
            
            <Route path="/admin/dashboard" element={
                <PrivateRoute allowedTypes={['admin']}>
                    <AdminDashboard />
                </PrivateRoute>
            } />
            
            <Route path="/jobs/:id" element={
                <PrivateRoute>
                    <JobDetails />
                </PrivateRoute>
            } />
            
            <Route path="/company/jobs/:id" element={
                <PrivateRoute allowedTypes={['company']}>
                    <RoleDetailPage />
                </PrivateRoute>
            } />
            
            <Route path="/messages" element={
                <PrivateRoute>
                    <Messages />
                </PrivateRoute>
            } />
            
            <Route path="/trials" element={
                <PrivateRoute>
                    <TrialsPage />
                </PrivateRoute>
            } />
            
            <Route path="/company/trials" element={
                <PrivateRoute allowedTypes={['company']}>
                    <TrialManagement />
                </PrivateRoute>
            } />
            
            <Route path="/company/trial/:bookingId/payment-success" element={
                <PrivateRoute allowedTypes={['company']}>
                    <PaymentSuccess />
                </PrivateRoute>
            } />
            
            <Route path="/company/trial/:bookingId/payment-cancelled" element={
                <PrivateRoute allowedTypes={['company']}>
                    <PaymentCancelled />
                </PrivateRoute>
            } />
            
            <Route path="/candidate/trials" element={
                <PrivateRoute allowedTypes={['candidate']}>
                    <TrialManagement />
                </PrivateRoute>
            } />

            {/* Catch-all — must stay last */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <div className="App">
                        <AppRoutes />
                        <Toaster position="top-right" />
                    </div>
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;